/**
 * Feedback text in screen space: PERFECT and SAVE pop over the character (a
 * fixed pool of bitmap texts that slam in, hold, and fade), and the link run
 * sits in the top-right corner as a steady counter, out of the way of the
 * player (owner, 2026-09-14). Set in the pixel typeface of the menus with a
 * dark stroke, so it reads over the night and the neon alike (the app loads
 * the typeface before any renderer is built). Everything is created once; text
 * changes only when a value does, from strings built up front, so a frame
 * allocates nothing.
 */

import { BitmapText, Container } from "pixi.js";
import type { Camera } from "./camera";
import type { RenderFeel } from "./feel";
import { NEON, PIXEL_FONT } from "./palette";
import type { ViewState } from "./view";

export const POPUP_POOL = 4;
/** LINK x N strings are prebuilt up to this count; longer runs reuse the last. */
const MAX_LINK_TEXT = 40;

export const PopupKind = {
  Perfect: 0,
  Save: 1,
} as const;

/** PERFECT is pink, SAVE cyan; the link counter is yellow, pink on a best run. */
const ACCENT = NEON.pink;
const PLAIN = NEON.cyan;
const LINK = NEON.yellow;
/** Stroke width at the 64 px the font is rendered at: one pixel of the pixel font. */
const STROKE_PX = 8;

function pixelStyle(): {
  fontFamily: string;
  fontSize: number;
  fill: number;
  stroke: { color: number; width: number };
} {
  return {
    fontFamily: PIXEL_FONT,
    fontSize: 64,
    fill: 0xffffff,
    stroke: { color: NEON.ink, width: STROKE_PX },
  };
}

export class Popups {
  readonly root = new Container();
  private readonly texts: BitmapText[] = [];
  private readonly startMs = new Float64Array(POPUP_POOL);
  private readonly scale = new Float64Array(POPUP_POOL);
  private readonly linkTexts: string[] = [];
  /** The corner link counter: its text, the run it shows, when it last grew, and when the run ended. */
  private readonly link: BitmapText;
  private linkCount = 0;
  private linkBest = false;
  private linkPulseMs = -1e9;
  private linkEndMs = 1e18;

  constructor() {
    for (let i = 0; i <= MAX_LINK_TEXT; i++) this.linkTexts.push(`LINK x${i}`);
    this.link = new BitmapText({ text: "LINK x0", style: pixelStyle() });
    this.link.anchor.set(1, 0);
    this.link.visible = false;
    this.root.addChild(this.link);
    for (let i = 0; i < POPUP_POOL; i++) {
      const t = new BitmapText({ text: "PERFECT", style: pixelStyle() });
      t.anchor.set(0.5, 1);
      t.visible = false;
      this.texts.push(t);
      this.root.addChild(t);
      this.startMs[i] = -1e9;
    }
  }

  /** The link run grew to `count` (a new best when `best`): the corner counter updates with a small pulse. */
  linkGrew(count: number, best: boolean, nowMs: number): void {
    this.linkCount = count;
    this.linkBest = best;
    this.linkPulseMs = nowMs;
    this.linkEndMs = 1e18;
    const text = this.linkTexts[count < MAX_LINK_TEXT ? count : MAX_LINK_TEXT]!;
    if (this.link.text !== text) this.link.text = text;
  }

  /** The link run ended: the counter holds for linkHoldMs, then fades. */
  linkEnded(nowMs: number): void {
    if (this.linkCount > 0 && this.linkEndMs > nowMs) this.linkEndMs = nowMs;
  }

  /**
   * Show a popup. A popup of the same kind that is still on screen holds longer instead of slamming
   * in again or stacking a copy (a catch, a pump, and a release can all be PERFECT inside half a
   * second, and re-slamming each read as flashing); otherwise the oldest slot is reused. `accent`
   * draws it pink.
   */
  push(kind: number, accent: boolean, nowMs: number, feel: RenderFeel): void {
    const life = feel.popupInMs + feel.popupHoldMs + feel.popupOutMs;
    let slot = -1;
    for (let i = 0; i < POPUP_POOL; i++) {
      const age = nowMs - this.startMs[i]!;
      if (this.kinds[i] === kind && age >= 0 && age < life) slot = i;
    }
    if (slot >= 0) {
      // Already showing: restart its hold, past the slam, without flashing.
      const age = nowMs - this.startMs[slot]!;
      if (age >= feel.popupInMs) this.startMs[slot] = nowMs - feel.popupInMs;
      if (accent) this.texts[slot]!.tint = ACCENT;
      return;
    }
    if (slot < 0) {
      slot = 0;
      for (let i = 1; i < POPUP_POOL; i++) if (this.startMs[i]! < this.startMs[slot]!) slot = i;
    }
    this.kinds[slot] = kind;
    const t = this.texts[slot]!;
    const text = kind === PopupKind.Save ? "SAVE" : "PERFECT";
    if (t.text !== text) t.text = text;
    t.tint = accent ? ACCENT : PLAIN;
    this.startMs[slot] = nowMs;
    this.scale[slot] = kind === PopupKind.Save ? 0.8 : 1;
  }

  clear(): void {
    for (let i = 0; i < POPUP_POOL; i++) {
      this.startMs[i] = -1e9;
      this.texts[i]!.visible = false;
    }
    this.linkCount = 0;
    this.linkEndMs = 1e18;
    this.link.visible = false;
  }

  draw(view: ViewState, camera: Camera, feel: RenderFeel, nowMs: number): void {
    this.drawLink(camera, feel, nowMs);
    const life = feel.popupInMs + feel.popupHoldMs + feel.popupOutMs;
    const px = camera.toScreenX(view.x);
    const headY = view.y + view.height + feel.popupAbove;
    const unit = (feel.popupSize / 64) * feel.popupScale * (camera.screenH / 1080);
    // Stack live popups upward, newest lowest.
    let stackY = camera.toScreenY(headY);
    for (let k = 0; k < POPUP_POOL; k++) {
      // Newest first: pick the k-th newest without allocating (pool of four, a scan per rank).
      let slot = -1;
      let newest = -1e18;
      for (let i = 0; i < POPUP_POOL; i++) {
        const s = this.startMs[i]!;
        const age = nowMs - s;
        if (age < 0 || age >= life) continue;
        let taken = false;
        for (let j = 0; j < k; j++) if (this.order[j] === i) taken = true;
        if (!taken && s > newest) {
          newest = s;
          slot = i;
        }
      }
      this.order[k] = slot;
      if (slot < 0) continue;
      const t = this.texts[slot]!;
      const age = nowMs - this.startMs[slot]!;
      let s = 1;
      let a = 1;
      if (age < feel.popupInMs) {
        // Slam: from large to size, quickly.
        const f = age / Math.max(1, feel.popupInMs);
        s = 1.8 - 0.8 * f;
        a = f;
      } else if (age > feel.popupInMs + feel.popupHoldMs) {
        const f = (age - feel.popupInMs - feel.popupHoldMs) / Math.max(1, feel.popupOutMs);
        a = 1 - f;
        s = 1 + 0.1 * f;
      }
      t.visible = true;
      t.alpha = a < 0 ? 0 : a;
      const sc = s * unit * this.scale[slot]!;
      t.scale.set(sc, sc);
      t.position.set(px, stackY);
      // The next one sits above this one's type height, whatever the zoom.
      stackY -= 64 * unit * this.scale[slot]! * feel.popupStack;
    }
    for (let i = 0; i < POPUP_POOL; i++) {
      const age = nowMs - this.startMs[i]!;
      if (age < 0 || age >= life) this.texts[i]!.visible = false;
    }
  }

  /** The corner counter: steady while the run lives, a short pulse when it grows, a fade after it ends. */
  private drawLink(camera: Camera, feel: RenderFeel, nowMs: number): void {
    const t = this.link;
    const sinceEnd = nowMs - this.linkEndMs;
    if (this.linkCount <= 0 || sinceEnd >= feel.linkHoldMs + feel.popupOutMs || nowMs < this.linkPulseMs) {
      t.visible = false;
      return;
    }
    const k = camera.screenH / 1080;
    const pulse = feel.linkPulseMs > 0 ? Math.max(0, 1 - (nowMs - this.linkPulseMs) / feel.linkPulseMs) : 0;
    const sc = (feel.linkSize / 64) * k * (1 + feel.linkPulseScale * pulse);
    t.visible = true;
    t.alpha =
      sinceEnd > feel.linkHoldMs
        ? Math.max(0, 1 - (sinceEnd - feel.linkHoldMs) / Math.max(1, feel.popupOutMs))
        : 1;
    t.tint = this.linkBest ? ACCENT : LINK;
    t.scale.set(sc, sc);
    t.position.set(camera.screenW - feel.linkMargin * k, feel.linkTop * k);
  }

  private readonly order = new Int32Array(POPUP_POOL);
  private readonly kinds = new Int32Array(POPUP_POOL).fill(-1);
}
